import { useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Button, makeStyles, tokens, Text } from '@coreai/fluentui-react';
import { Add20Regular } from '@fluentui/react-icons';
import { useAppStore } from '@/stores/appStore';
import { useCurrentApp } from '@/hooks/useCurrentApp';
import { useCurrentProject } from '@/hooks/useCurrentProject';
import { useAppWorkflows } from '@/hooks/useAppResource';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { AppOverviewHeader } from '@/components/shared/AppOverviewHeader';
import { PageSection } from '@/components/shared/PageSection';
import {
  ConnectionsFooterActions,
  ConnectionsView,
} from '@/components/ConnectionList/ConnectionsView';
import { useConnectionsEditor } from '@/components/ConnectionList/useConnectionsEditor';
import { AddConnectionDialog } from '@/components/ConnectionList/AddConnectionDialog';
import { useTelemetry } from '@/hooks/useTelemetry';

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  footer: {
    paddingTop: tokens.spacingVerticalM,
    marginTop: tokens.spacingVerticalM,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalXXXL,
    color: tokens.colorNeutralForeground3,
  },
});

/**
 * ConnectionsPage — full-page wrapper around `ConnectionsView`. Mirrors
 * `ConnectionsDialog` so the route and the modal stay behaviorally identical.
 *
 * Renders the standard app shell (`AppOverviewHeader` + `PageSection`) for
 * consistency with the other app-scoped pages.
 */
export const ConnectionsPage = () => {
  const styles = useStyles();
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const { trackEvent } = useTelemetry();
  const { app, appName, locationName } = useCurrentApp();
  const { projectName: routeProjectName } = useCurrentProject();
  const selectedApp = useAppStore((s) => s.selectedApp);
  // Connections editor was historically wired through the store-based
  // selection. Keep that wiring intact so we don't perturb the editor
  // subsystem, but power the page chrome from URL-derived context.
  // The URL is the single source of truth for the project so a stale
  // Zustand selection can't trick the editor into reading/writing the wrong
  // project. A query-string project is only a fallback outside hierarchical
  // app routes; the route project must stay paired with its resolved app and
  // managed identity.
  const editorProjectName = routeProjectName ?? searchParams.get('project') ?? '';
  const selectedMatchesRoute =
    !!selectedApp?.name && !!appName && selectedApp.name.toLowerCase() === appName.toLowerCase();
  const routeMatchedApp = selectedMatchesRoute ? selectedApp : undefined;
  const editorAppName = appName ?? routeMatchedApp?.name ?? '';
  const editorAppId = app?.properties.appResourceId ?? routeMatchedApp?.properties.appResourceId ?? '';

  useDocumentTitle(['Connections', editorAppName, editorProjectName]);

  const { data: workflows } = useAppWorkflows(app ?? null);
  const state = useConnectionsEditor({
    projectName: editorProjectName,
    appName: editorAppName,
    appId: editorAppId,
    ...(app?.identity?.principalId
      ? { appPrincipalId: app.identity.principalId }
      : routeMatchedApp?.identity?.principalId
        ? { appPrincipalId: routeMatchedApp.identity.principalId }
        : {}),
    enabled: !!editorAppName && !!editorAppId,
  });

  useEffect(() => {
    setIsAddOpen(false);
  }, [editorAppName, editorProjectName]);

  const headerName = editorAppName || appName || '';
  const addConnectionAction =
    !state.permissionsLoading && state.userCanEdit ? (
      <Button
        appearance="primary"
        icon={<Add20Regular aria-hidden="true" />}
        onClick={() => {
          trackEvent('connections.creation.opened', {
            defaultFamily: state.selectedTab,
          });
          setIsAddOpen(true);
        }}
      >
        Add connection
      </Button>
    ) : undefined;
  const addConnectionDialog = (
    <AddConnectionDialog
      key={`${editorProjectName}/${editorAppName}`}
      open={isAddOpen}
      onClose={() => setIsAddOpen(false)}
      state={state}
    />
  );

  if (!headerName) {
    return (
      <div className={styles.page}>
        <PageSection
          title="Connections"
          actions={addConnectionAction}
        >
          <div className={styles.empty}>
            <Text>Select an application to view connections.</Text>
          </div>
        </PageSection>
        {addConnectionDialog}
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <AppOverviewHeader
        name={headerName}
        app={app}
        region={locationName}
        workflowCount={workflows?.length ?? 0}
      />
      <PageSection title="Connections" actions={addConnectionAction}>
        <div className={styles.body}>
          <ConnectionsView state={state} />
        </div>
        {state.selectedTab === 'raw' && (
          <div className={styles.footer}>
            <ConnectionsFooterActions state={state} />
          </div>
        )}
      </PageSection>
      {addConnectionDialog}
    </div>
  );
};

ConnectionsPage.displayName = 'ConnectionsPage';
